// src/components/dashboard/Dashboard.js - Updated with TaskDashboard styling
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchTasks } from '../../features/tasks/taskSlice';
import { fetchUsers } from '../../features/admin/adminSlice';
import {
    FaTasks,
    FaChartLine,
    FaUsers,
    FaCheck,
    FaExclamationTriangle,
    FaClock,
    FaPlus,
    FaBell,
    FaClipboardList,
    FaCog,
    FaUserCog
} from 'react-icons/fa';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const Dashboard = () => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);
    const { users } = useSelector(state => state.admin);
    const { tasks, isLoading: tasksLoading } = useSelector(state => state.tasks);

    // Dashboard stats
    const [stats, setStats] = useState({
        totalTasks: 0,
        completedTasks: 0,
        urgentTasks: 0,
        teamMembers: 0,
        avgTaskPriority: 0,
        avgCompletionTime: 0
    });

    // Recent tasks
    const [recentTasks, setRecentTasks] = useState([]);

    // Activity data for the chart
    const [activityData, setActivityData] = useState({
        labels: [],
        datasets: [
            {
                label: 'Tasks Created',
                data: [],
                borderColor: '#667eea',
                backgroundColor: 'rgba(102, 126, 234, 0.2)',
                borderWidth: 3,
                tension: 0.4,
            },
            {
                label: 'Tasks Completed',
                data: [],
                borderColor: '#28a745',
                backgroundColor: 'rgba(40, 167, 69, 0.2)',
                borderWidth: 3,
                tension: 0.4,
            }
        ],
    });

    // Priority distribution for chart
    const [priorityData, setPriorityData] = useState({
        labels: ['Low', 'Medium', 'High', 'Critical'],
        datasets: [
            {
                label: 'Tasks by Priority',
                data: [0, 0, 0, 0],
                backgroundColor: [
                    'rgba(52, 152, 219, 0.8)',
                    'rgba(46, 204, 113, 0.8)',
                    'rgba(241, 196, 15, 0.8)',
                    'rgba(231, 76, 60, 0.8)',
                ],
                borderColor: [
                    '#3498db',
                    '#2ecc71',
                    '#f1c40f',
                    '#e74c3c',
                ],
                borderWidth: 2,
                borderRadius: 8,
            },
        ],
    });

    // Notifications
    const [notifications, setNotifications] = useState([
        {
            id: 1,
            message: 'New task assigned to you: "Update user documentation"',
            time: '2 hours ago',
            read: false
        },
        {
            id: 2,
            message: 'Task "Fix login page bug" has been marked as completed',
            time: '5 hours ago',
            read: true
        },
        {
            id: 3,
            message: 'Your task "Implement new features" is due tomorrow',
            time: '1 day ago',
            read: false
        }
    ]);

    useEffect(() => {
        console.log('🚀 Dashboard mounted, fetching data...');
        dispatch(fetchTasks());
        if (['admin', 'super_admin'].includes(user?.role)) {
            dispatch(fetchUsers());
        }
    }, [dispatch, user?.role]);

    useEffect(() => {
        console.log('=== DASHBOARD DEBUG ===');
        console.log('Tasks:', tasks);
        console.log('Tasks length:', tasks?.length);
        console.log('Users:', users);
        console.log('Users length:', users?.length);
        console.log('Tasks loading:', tasksLoading);
        console.log('User role:', user?.role);
        console.log('=======================');
    }, [tasks, users, tasksLoading, user?.role]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                console.log('🔄 Dashboard visible again, refreshing tasks...');
                dispatch(fetchTasks());
                if (['admin', 'super_admin'].includes(user?.role)) {
                    dispatch(fetchUsers());
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        const interval = setInterval(() => {
            console.log('🔄 Periodic refresh of dashboard data...');
            dispatch(fetchTasks());
        }, 30000);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(interval);
        };
    }, [dispatch, user?.role]);

    useEffect(() => {
        console.log('📊 Calculating dashboard stats...');

        if (tasks && Array.isArray(tasks) && tasks.length > 0) {
            console.log('✅ Processing', tasks.length, 'tasks');

            const completedTasks = tasks.filter(task => {
                const isCompleted = task.status === 'completed';
                if (isCompleted) {
                    console.log(`✅ Found completed task: ${task.id} - ${task.title.substring(0, 20)}...`);
                }
                return isCompleted;
            });

            console.log(`📊 COMPLETED TASKS COUNT: ${completedTasks.length}`);

            const urgent = tasks.filter(task => (task.urgency || 0) >= 8).length;
            const avgPriority = tasks.reduce((acc, task) => acc + (task.priority_score || 0), 0) / tasks.length;

            const completedTasksWithTime = tasks.filter(task =>
                task.status === 'completed' && task.completion_time && !isNaN(task.completion_time)
            );
            const avgTime = completedTasksWithTime.length > 0
                ? completedTasksWithTime.reduce((acc, task) => acc + task.completion_time, 0) / completedTasksWithTime.length
                : 0;

            const newStats = {
                totalTasks: tasks.length,
                completedTasks: completedTasks.length,
                urgentTasks: urgent,
                teamMembers: users && Array.isArray(users) ? users.length : 0,
                avgTaskPriority: avgPriority.toFixed(1),
                avgCompletionTime: avgTime.toFixed(1)
            };

            console.log('📈 NEW STATS CALCULATED:', newStats);
            setStats(newStats);

            // Get recent tasks
            const sorted = [...tasks].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setRecentTasks(sorted.slice(0, 5));

            // Generate activity data
            const last7Days = Array.from({ length: 7 }, (_, i) => {
                const date = new Date();
                date.setDate(date.getDate() - (6 - i));
                return date.toLocaleDateString('en-US', { weekday: 'short' });
            });

            const createdData = last7Days.map(day => {
                return tasks.filter(task => {
                    if (!task.created_at) return false;
                    const taskDate = new Date(task.created_at);
                    return taskDate.toLocaleDateString('en-US', { weekday: 'short' }) === day;
                }).length;
            });

            const completedData = last7Days.map(day => {
                return tasks.filter(task => {
                    if (!task.completed_at) return false;
                    const taskDate = new Date(task.completed_at);
                    return taskDate.toLocaleDateString('en-US', { weekday: 'short' }) === day;
                }).length;
            });

            setActivityData({
                labels: last7Days,
                datasets: [
                    {
                        label: 'Tasks Created',
                        data: createdData,
                        borderColor: '#667eea',
                        backgroundColor: 'rgba(102, 126, 234, 0.2)',
                        borderWidth: 3,
                        tension: 0.4,
                    },
                    {
                        label: 'Tasks Completed',
                        data: completedData,
                        borderColor: '#28a745',
                        backgroundColor: 'rgba(40, 167, 69, 0.2)',
                        borderWidth: 3,
                        tension: 0.4,
                    }
                ],
            });

            // Generate priority distribution
            const lowPriority = tasks.filter(task => (task.priority_score || 0) >= 1 && (task.priority_score || 0) <= 3).length;
            const mediumPriority = tasks.filter(task => (task.priority_score || 0) > 3 && (task.priority_score || 0) <= 6).length;
            const highPriority = tasks.filter(task => (task.priority_score || 0) > 6 && (task.priority_score || 0) <= 8).length;
            const criticalPriority = tasks.filter(task => (task.priority_score || 0) > 8).length;

            setPriorityData({
                labels: ['Low', 'Medium', 'High', 'Critical'],
                datasets: [
                    {
                        label: 'Tasks by Priority',
                        data: [lowPriority, mediumPriority, highPriority, criticalPriority],
                        backgroundColor: [
                            'rgba(52, 152, 219, 0.8)',
                            'rgba(46, 204, 113, 0.8)',
                            'rgba(241, 196, 15, 0.8)',
                            'rgba(231, 76, 60, 0.8)',
                        ],
                        borderColor: [
                            '#3498db',
                            '#2ecc71',
                            '#f1c40f',
                            '#e74c3c',
                        ],
                        borderWidth: 2,
                        borderRadius: 8,
                    },
                ],
            });
        } else {
            console.log('⚠️ No tasks available for stats calculation');
            setStats({
                totalTasks: 0,
                completedTasks: 0,
                urgentTasks: 0,
                teamMembers: users && Array.isArray(users) ? users.length : 0,
                avgTaskPriority: 0,
                avgCompletionTime: 0
            });
            setRecentTasks([]);
        }
    }, [tasks, users]);

    // Handle mark notification as read
    const handleMarkAsRead = (id) => {
        setNotifications(prevNotifications =>
            prevNotifications.map(notification =>
                notification.id === id
                    ? { ...notification, read: true }
                    : notification
            )
        );
    };

    // Add loading state
    if (tasksLoading) {
        return (
            <div className="dashboard-container">
                <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    // Chart options
    const lineChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: '600'
                    }
                }
            },
            title: {
                display: true,
                text: 'Task Activity (Last 7 Days)',
                font: {
                    size: 16,
                    weight: '700'
                },
                color: '#495057'
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    color: '#6c757d'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            x: {
                ticks: {
                    color: '#6c757d'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        },
        maintainAspectRatio: false,
    };

    const barChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: '600'
                    }
                }
            },
            title: {
                display: true,
                text: 'Tasks by Priority',
                font: {
                    size: 16,
                    weight: '700'
                },
                color: '#495057'
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    color: '#6c757d'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            },
            x: {
                ticks: {
                    color: '#6c757d'
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                }
            }
        },
        maintainAspectRatio: false,
    };

    // Add Quick Actions section for users
    const getUserQuickActions = () => (
        <div className="dashboard-section quick-actions-section">
            <div className="section-header">
                <h3>Quick Actions</h3>
            </div>

            <div className="quick-actions">
                <Link to="/tasks/new" className="quick-action-button">
                    <FaPlus />
                    <span>New Task</span>
                </Link>

                <Link to="/tasks/" className="quick-action-button">
                    <FaClipboardList />
                    <span>Import Tasks</span>
                </Link>

                <Link to="/profile" className="quick-action-button">
                    <FaUserCog />
                    <span>Edit Profile</span>
                </Link>

                <Link to="/tasks" className="quick-action-button">
                    <FaChartLine />
                    <span>View Reports</span>
                </Link>
            </div>
        </div>
    );

    // Role-based content
    const getDashboardContent = () => {
        if (['admin', 'super_admin'].includes(user?.role)) {
            return (
                <div className="admin-overview">
                    <h2>Admin Overview</h2>

                    <div className="admin-stats">
                        <div className="stat-card">
                            <div className="stat-icon users">
                                <FaUsers />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.teamMembers}</div>
                                <div className="stat-label">Users</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon tasks">
                                <FaTasks />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.totalTasks}</div>
                                <div className="stat-label">Total Tasks</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon completed">
                                <FaCheck />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">
                                    {stats.totalTasks ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0}%
                                </div>
                                <div className="stat-label">Completion Rate</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon urgent">
                                <FaExclamationTriangle />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.urgentTasks}</div>
                                <div className="stat-label">Urgent Tasks</div>
                            </div>
                        </div>
                    </div>

                    <div className="dashboard-charts">
                        <div className="chart-container">
                            <div className="chart-wrapper" style={{ height: '350px' }}>
                                <Line options={lineChartOptions} data={activityData} />
                            </div>
                        </div>

                        <div className="chart-container">
                            <div className="chart-wrapper" style={{ height: '350px' }}>
                                <Bar options={barChartOptions} data={priorityData} />
                            </div>
                        </div>
                    </div>

                    <div className="admin-actions">
                        <Link to="/admin/users" className="action-card">
                            <div className="action-icon">
                                <FaUserCog />
                            </div>
                            <div className="action-content">
                                <h3>User Management</h3>
                                <p>Manage users, roles, and permissions</p>
                            </div>
                        </Link>

                        <Link to="/tasks" className="action-card">
                            <div className="action-icon">
                                <FaClipboardList />
                            </div>
                            <div className="action-content">
                                <h3>Task Dashboard</h3>
                                <p>View and analyze tasks across all teams</p>
                            </div>
                        </Link>
                    </div>
                </div>
            );
        }

        return (
            <div className="user-overview">
                <h2>Your Dashboard</h2>

                <div className="user-stats">
                    <div className="stat-card">
                        <div className="stat-icon tasks">
                            <FaTasks />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.totalTasks}</div>
                            <div className="stat-label">My Tasks</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon completed">
                            <FaCheck />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.completedTasks}</div>
                            <div className="stat-label">Completed</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon urgent">
                            <FaExclamationTriangle />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.urgentTasks}</div>
                            <div className="stat-label">Urgent</div>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon avg-time">
                            <FaClock />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stats.avgCompletionTime}h</div>
                            <div className="stat-label">Avg. Completion Time</div>
                        </div>
                    </div>
                </div>

                <div className="dashboard-charts">
                    <div className="chart-container">
                        <div className="chart-wrapper" style={{ height: '350px' }}>
                            <Line options={lineChartOptions} data={activityData} />
                        </div>
                    </div>

                    <div className="chart-container">
                        <div className="chart-wrapper" style={{ height: '350px' }}>
                            <Bar options={barChartOptions} data={priorityData} />
                        </div>
                    </div>
                </div>

                <div className="user-dashboard-sections">
                    {getUserQuickActions()}
                </div>
            </div>
        );
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>Welcome, {user?.first_name}</h1>
                <p className="welcome-message">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
            </div>

            {getDashboardContent()}

            <div className="dashboard-section recent-tasks-section">
                <div className="section-header">
                    <h2>Recent Tasks</h2>
                    <Link to="/tasks" className="btn btn-outline-primary">View All</Link>
                </div>

                <div className="recent-tasks">
                    {recentTasks.length > 0 ? (
                        <div className="task-list">
                            {recentTasks.map(task => (
                                <div key={task.id} className="task-card">
                                    <div className={`priority-indicator p-${Math.round(task.priority_score || 0)}`}></div>
                                    <div className="task-content">
                                        <h3>{task.title}</h3>
                                        <p>{(task.description || '').substring(0, 100)}{(task.description || '').length > 100 ? '...' : ''}</p>
                                        <div className="task-meta">
                                            <span className="task-metric">
                                                <strong>Importance:</strong> {task.importance || 0}
                                            </span>
                                            <span className="task-metric">
                                                <strong>Urgency:</strong> {task.urgency || 0}
                                            </span>
                                            <span className="task-metric">
                                                <strong>Complexity:</strong> {task.complexity || 0}
                                            </span>
                                        </div>
                                        <div className="task-actions">
                                            <Link to={`/tasks/${task.id}`} className="btn-text">View Details</Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>No tasks found. Start by adding a new task.</p>
                            <Link to="/tasks/new" className="btn btn-primary">
                                <FaPlus /> Add Task
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Modern Dashboard Styles - Matching TaskDashboard */}
            <style jsx>{`
                /* Dashboard Container */
                .dashboard-container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 20px;
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    min-height: calc(100vh - var(--header-height, 60px) - var(--footer-height, 120px));
                    border-radius: 12px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                }

                /* Dashboard Header */
                .dashboard-header {
                    text-align: center;
                    margin-bottom: 40px;
                    padding: 30px 20px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 16px;
                    color: white;
                    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
                    position: relative;
                    overflow: hidden;
                }

                .dashboard-header::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
                    animation: float 6s ease-in-out infinite;
                }

                @keyframes float {
                    0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
                    50% { transform: translate(-50%, -50%) rotate(180deg); }
                }

                .dashboard-header h1 {
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin-bottom: 15px;
                    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
                    position: relative;
                    z-index: 1;
                }

                .welcome-message {
                    font-size: 1.2rem;
                    opacity: 0.9;
                    font-weight: 300;
                    position: relative;
                    z-index: 1;
                    margin: 0;
                }

                /* Loading State */
                .loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    margin-bottom: 30px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                }

                .loading-spinner {
                    width: 60px;
                    height: 60px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #667eea;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 20px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .loading p {
                    font-size: 18px;
                    color: #667eea;
                    font-weight: 600;
                    margin: 0;
                    animation: pulse 2s ease-in-out infinite;
                }

                @keyframes pulse {
                    0%, 100% { opacity: 0.7; }
                    50% { opacity: 1; }
                }

                /* Overview Sections */
                .admin-overview,
                .user-overview {
                    margin-bottom: 30px;
                }

                .admin-overview h2,
                .user-overview h2 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #495057;
                    margin-bottom: 30px;
                    text-align: center;
                }

                /* Stats Cards */
                .admin-stats,
                .user-stats {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 25px;
                    margin-bottom: 40px;
                }

                .stat-card {
                    background: white;
                    border-radius: 16px;
                    padding: 30px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.05), transparent);
                    transition: left 0.6s;
                }

                .stat-card:hover::before {
                    left: 100%;
                }

                .stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }

                .stat-icon {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    color: white;
                    position: relative;
                    z-index: 2;
                }

                .stat-icon.users {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .stat-icon.tasks {
                    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
                }

                .stat-icon.completed {
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                }

                .stat-icon.urgent {
                    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
                }

                .stat-icon.avg-time {
                    background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
                }

                .stat-content {
                    flex: 1;
                    position: relative;
                    z-index: 2;
                }

                .stat-value {
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #495057;
                    line-height: 1;
                    margin-bottom: 8px;
                }

                .stat-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #6c757d;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                /* Charts Section */
                .dashboard-charts {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
                    gap: 30px;
                    margin-bottom: 40px;
                }

                .chart-container {
                    background: white;
                    border-radius: 16px;
                    padding: 30px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .chart-container:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }

                .chart-wrapper {
                    position: relative;
                }

                /* Admin Actions */
                .admin-actions {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 25px;
                    margin-bottom: 40px;
                }

                .action-card {
                    background: white;
                    border-radius: 16px;
                    padding: 30px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    text-decoration: none;
                    color: inherit;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                }

                .action-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
                    transition: left 0.6s;
                }

                .action-card:hover::before {
                    left: 100%;
                }

                .action-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                    text-decoration: none;
                    color: inherit;
                }

                .action-icon {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    color: white;
                    position: relative;
                    z-index: 2;
                }

                .action-content {
                    flex: 1;
                    position: relative;
                    z-index: 2;
                }

                .action-content h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 8px;
                }

                .action-content p {
                    font-size: 14px;
                    color: #6c757d;
                    margin: 0;
                }

                /* Dashboard Sections */
                .dashboard-section {
                    background: white;
                    border-radius: 16px;
                    padding: 30px;
                    margin-bottom: 30px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .dashboard-section:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }

                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 25px;
                    padding-bottom: 15px;
                    border-bottom: 2px solid #e9ecef;
                }

                .section-header h2,
                .section-header h3 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #495057;
                    margin: 0;
                }

                /* Quick Actions */
                .quick-actions {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                }

                .quick-action-button {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 25px 20px;
                    background: #f8f9fa;
                    border: 2px solid #e9ecef;
                    border-radius: 12px;
                    text-decoration: none;
                    color: #495057;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                }

                .quick-action-button::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
                    transition: left 0.6s;
                }

                .quick-action-button:hover::before {
                    left: 100%;
                }

                .quick-action-button:hover {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border-color: #667eea;
                    transform: translateY(-3px);
                    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
                    text-decoration: none;
                }

                .quick-action-button svg {
                    font-size: 28px;
                    margin-bottom: 12px;
                    position: relative;
                    z-index: 2;
                }

                .quick-action-button span {
                    font-weight: 600;
                    font-size: 14px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    position: relative;
                    z-index: 2;
                }

                /* Recent Tasks */
                .recent-tasks {
                    margin-top: 20px;
                }

                .task-list {
                    display: grid;
                    gap: 20px;
                }

                .task-card {
                    background: #f8f9fa;
                    border: 2px solid #e9ecef;
                    border-radius: 12px;
                    padding: 25px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    gap: 20px;
                }

                .task-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.05), transparent);
                    transition: left 0.6s;
                }

                .task-card:hover::before {
                    left: 100%;
                }

                .task-card:hover {
                    border-color: #667eea;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
                }

                .priority-indicator {
                    width: 8px;
                    border-radius: 4px;
                    position: relative;
                    z-index: 2;
                }

                .priority-indicator.p-1,
                .priority-indicator.p-2,
                .priority-indicator.p-3 {
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                }

                .priority-indicator.p-4,
                .priority-indicator.p-5,
                .priority-indicator.p-6 {
                    background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
                }

                .priority-indicator.p-7,
                .priority-indicator.p-8 {
                    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
                }

                .priority-indicator.p-9,
                .priority-indicator.p-10 {
                    background: linear-gradient(135deg, #dc3545 0%, #6f42c1 100%);
                }

                .task-content {
                    flex: 1;
                    position: relative;
                    z-index: 2;
                }

                .task-content h3 {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 10px;
                }

                .task-content p {
                    color: #6c757d;
                    font-size: 14px;
                    line-height: 1.5;
                    margin-bottom: 15px;
                }

                .task-meta {
                    display: flex;
                    gap: 15px;
                    margin-bottom: 15px;
                    flex-wrap: wrap;
                }

                .task-metric {
                    font-size: 12px;
                    color: #6c757d;
                    background: white;
                    padding: 5px 10px;
                    border-radius: 6px;
                    border: 1px solid #dee2e6;
                }

                .task-metric strong {
                    color: #495057;
                }

                .task-actions {
                    display: flex;
                    gap: 15px;
                }

                /* Buttons */
                .btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 20px;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 14px;
                    text-decoration: none;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 2px solid;
                    cursor: pointer;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    position: relative;
                    overflow: hidden;
                }

                .btn::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    transition: left 0.6s;
                }

                .btn:hover::before {
                    left: 100%;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border-color: #667eea;
                }

                .btn-primary:hover {
                    background: linear-gradient(135deg, #5a6fd8 0%, #6c4f9c 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
                    text-decoration: none;
                    color: white;
                }

                .btn-outline-primary {
                    background: transparent;
                    color: #667eea;
                    border-color: #667eea;
                }

                .btn-outline-primary:hover {
                    background: #667eea;
                    color: white;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
                    text-decoration: none;
                }

                .btn-text {
                    background: none;
                    border: none;
                    color: #667eea;
                    font-weight: 600;
                    font-size: 14px;
                    text-decoration: none;
                    padding: 8px 0;
                    transition: all 0.3s;
                    cursor: pointer;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .btn-text:hover {
                    color: #764ba2;
                    text-decoration: none;
                    transform: translateX(5px);
                }

                /* Empty State */
                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                    background: #f8f9fa;
                    border: 2px dashed #dee2e6;
                    border-radius: 12px;
                    position: relative;
                    overflow: hidden;
                }

                .empty-state::before {
                    content: '📊';
                    font-size: 60px;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    opacity: 0.1;
                    z-index: 1;
                }

                .empty-state p {
                    font-size: 16px;
                    color: #6c757d;
                    margin-bottom: 20px;
                    position: relative;
                    z-index: 2;
                }

                .empty-state .btn {
                    position: relative;
                    z-index: 2;
                }

                /* User Dashboard Sections */
                .user-dashboard-sections {
                    display: grid;
                    gap: 30px;
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .dashboard-container {
                        padding: 15px;
                        margin: 10px;
                    }

                    .dashboard-header {
                        padding: 20px 15px;
                        margin-bottom: 20px;
                    }

                    .dashboard-header h1 {
                        font-size: 2rem;
                    }

                    .welcome-message {
                        font-size: 1rem;
                    }

                    .admin-stats,
                    .user-stats {
                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                        gap: 15px;
                    }

                    .stat-card {
                        padding: 20px;
                    }

                    .stat-value {
                        font-size: 2rem;
                    }

                    .dashboard-charts {
                        grid-template-columns: 1fr;
                        gap: 20px;
                    }

                    .chart-container {
                        padding: 20px;
                    }

                    .admin-actions {
                        grid-template-columns: 1fr;
                        gap: 15px;
                    }

                    .action-card {
                        padding: 20px;
                    }

                    .dashboard-section {
                        padding: 20px;
                        margin-bottom: 20px;
                    }

                    .section-header {
                        flex-direction: column;
                        gap: 15px;
                        align-items: flex-start;
                    }

                    .quick-actions {
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: 15px;
                    }

                    .quick-action-button {
                        padding: 20px 15px;
                    }

                    .task-card {
                        padding: 20px;
                        flex-direction: column;
                        gap: 15px;
                    }

                    .task-meta {
                        gap: 10px;
                    }

                    .task-actions {
                        flex-direction: column;
                        gap: 10px;
                    }
                }

                @media (max-width: 480px) {
                    .dashboard-header h1 {
                        font-size: 1.5rem;
                    }

                    .welcome-message {
                        font-size: 0.9rem;
                    }

                    .admin-stats,
                    .user-stats {
                        grid-template-columns: 1fr;
                    }

                    .stat-card {
                        padding: 15px;
                        gap: 15px;
                    }

                    .stat-icon {
                        width: 50px;
                        height: 50px;
                        font-size: 20px;
                    }

                    .stat-value {
                        font-size: 1.8rem;
                    }

                    .quick-actions {
                        grid-template-columns: 1fr;
                    }

                    .chart-container {
                        padding: 15px;
                    }
                }

                /* Success State Animation */
                @keyframes slideInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .dashboard-section,
                .stat-card,
                .action-card {
                    animation: slideInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
                }

                /* Focus States for Accessibility */
                .btn:focus,
                .quick-action-button:focus,
                .action-card:focus {
                    outline: 2px solid #667eea;
                    outline-offset: 2px;
                }

                /* Print Styles */
                @media print {
                    .dashboard-container {
                        background: white;
                        box-shadow: none;
                        padding: 20px;
                    }

                    .dashboard-header {
                        background: #f8f9fa;
                        color: #333;
                    }

                    .quick-actions-section,
                    .chart-container {
                        break-inside: avoid;
                    }

                    .btn,
                    .quick-action-button {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default Dashboard;